class gTagEvents {
    constructor() {
        this.delegateBody = new Delegate(document.body);
        this.lastAddToCartButtonId = "";
        this.lastAddToCartContext = "";
        this.lastAddToCartAt = 0;
        this.lastHomepageCarouselIndex = 0;
        this.recentHomepageEvents = {};

        // Track which ATC button initiated the latest add-to-cart.
        // We use capture phase so we record it even if other code stops propagation.
        document.addEventListener(
          "click",
          (event) => {
            const button = event.target?.closest?.('button, input[type="submit"]');
            const atc = event.target?.closest?.('[data-action="add-to-cart"], [data-product-add-to-cart-button], [name="add"]');
            if (!button && !atc) return;

            const atcEl = atc || button;
            const inUpsell = !!atcEl?.closest?.(".upsell__block");
            const inMiniCartRecommendations = !!atcEl?.closest?.(".mini-cart__recommendations");
            const homepageCarousel = this.isHomepage() ? atcEl?.closest?.("[data-gtm-carousel]") : null;

            // Sticky ATC button has a stable id. Record it explicitly.
            if (button?.id === "StickyAddToCart") {
              this.lastAddToCartButtonId = "StickyAddToCart";
              this.lastAddToCartContext = "sticky";
              this.lastAddToCartAt = Date.now();
              return;
            }

            // Also record other ATC-like buttons so the sticky marker doesn't "stick" forever.
            if (
              atcEl?.matches?.('[data-product-add-to-cart-button], [data-action="add-to-cart"], [name="add"]') ||
              atcEl?.getAttribute?.("type") === "submit"
            ) {
              this.lastAddToCartButtonId = button?.id || "";
              this.lastAddToCartContext = inMiniCartRecommendations
                ? "mini_cart_recommendations"
                : inUpsell
                  ? "upsell"
                  : homepageCarousel
                    ? "homepage_carousel"
                    : "";
              this.lastHomepageCarouselIndex = homepageCarousel ? this.getHomepageElementIndex(homepageCarousel, "[data-gtm-carousel]") : 0;
              this.lastAddToCartAt = Date.now();
            }
          },
          true
        );

        this.initIntersectionObserver();
        this.initFireworkTracking();
        this.interceptCartApi();
        this.bindEvents();
        this.observeDOMChanges();
        this.domChanged();
    }

    bindEvents() {
        this.delegateBody.on("filters:sort:changed", () => { this.sendSortByData(); this.sendFiltersData(); });
        this.delegateBody.on("click", '[name="checkout"]', this.handleCheckout.bind(this));
        this.delegateBody.on("filters:cleared", this.clearFiltersData.bind(this));
        this.delegateBody.on("facet:criteria-changed", this.sendSortByData.bind(this));
        this.delegateBody.on("click", '[data-action="ga-send-select"], [data-ga-action="ga-send-select"]', this.handleSelectItem.bind(this));
        this.delegateBody.on("click", ".cartFooter--bottomLink", this.handleCartContinueShopping.bind(this));
        this.delegateBody.on("click", ".metaButtons--wrapper .metaButton", this.handleCollectionPageClick.bind(this));
        this.delegateBody.on("click", ".product__info-headings .swiper-slide", this.handleProductPageClick.bind(this));
        this.delegateBody.on("click", "div[oke-quiz] .c-startPanel-button", this.handleQuizStartClick.bind(this));
        this.delegateBody.on("input", "div[oke-quiz] c-emailInput input[type=\"email\"]", this.handleQuizEmailStepClick.bind(this));
        this.delegateBody.on("click", "div[oke-quiz] button.c-questionsForm-next--email", this.handleQuizCompletionClick.bind(this));
        // Covers pressing Enter in the email field (form submit), not just clicking the submit button.
        this.delegateBody.on("submit", "div[oke-quiz] form", this.handleQuizCompletionSubmit.bind(this));
        this.delegateBody.on("click", ".product__media", this.handleProductImageGalleryClick.bind(this));
        this.delegateBody.on("touchstart", ".product flickity-carousel.product__media-list .flickity-slider, .product flickity-carousel.product__media-list", this.handleProductImageSwipeTouchStart.bind(this));
        this.delegateBody.on("click", "div[oke-star-rating]", this.handleProductRatingsInteraction.bind(this));
        this.delegateBody.on("click", ".connected-items__wrapper a.SelectboxLabelNormal[data-title]", this.handleProductSizeClick.bind(this));
        this.delegateBody.on("click", ".SelectBox--WrapperColor a.SelectboxCircle[data-title], .SelectBox--InnerWrapperColor a.SelectboxCircle[data-title]", this.handleProductColorClick.bind(this));
        this.delegateBody.on("click", ".productInfo--wraper .quiz--button", this.handleProductQuizCTAClick.bind(this));
        this.delegateBody.on("click", ".product__info-wrapper .swiper-slide", this.handleProductTabsClick.bind(this));
        this.delegateBody.on("click", ".Product--ExpandButton", this.handleProductViewAllIngredientsClick.bind(this));
        this.delegateBody.on("click", "#section-firework-embed, .section-firework-embed, section-firework-embed", this.handleProductVideoClick.bind(this));
        this.delegateBody.on("click", ".oke-w-navBar-item", this.handleProductReviewsTabClick.bind(this));
        this.delegateBody.on("click", ".upsell__block .upsell__tab", this.handleProductRecommendedCarouselTabClick.bind(this));
        this.delegateBody.on("click", '.upsell__block [data-action="show-more"]', this.handleProductRecommendedCarouselViewMoreClick.bind(this));
        this.delegateBody.on("click", ".mini-cart__recommendations .swiperButton", this.handleCartRecommendedCarouselArrowsClick.bind(this));
        this.delegateBody.on("click", ".blogPosts--container .seeAll--Button", this.handleProductBlogSeeAllArticlesClick.bind(this));
        this.delegateBody.on("click", ".blogPosts--container .article--Button", this.handleProductBlogReadClick.bind(this));
        this.delegateBody.on("click", ".productRecommendations .cardProduct", this.handleCartRecommendedCarouselClick.bind(this));
        this.delegateBody.on("click", "a.mobile-menu-quiz", this.handleNavigationHairQuizClick.bind(this));

        // Homepage tracking. Each selector matches a single wrapper element so the
        // delegate fires the handler exactly once per click, whatever was clicked inside.
        this.delegateBody.on("click", "[data-gtm-banner]", this.handleHomepageBannerClick.bind(this));
        this.delegateBody.on("click", "[data-gtm-quick-collections]", this.handleHomepageQuickCollectionsClick.bind(this));
        this.delegateBody.on("click", "[data-gtm-carousel]", this.handleHomepageCarouselClick.bind(this));
        this.delegateBody.on("click", "[data-gtm-collectionbox]", this.handleHomepageCollectionBoxClick.bind(this));
        this.delegateBody.on("click", "[data-gtm-app]", this.handleHomepageAppSectionClick.bind(this));
        this.delegateBody.on("click", '[data-action="selectbox-change"], [data-action="selectbox-link"], [data-gtm-market]', this.handleHomepageMarketSelectorClick.bind(this));

        // Video events are relayed by VideoComponent as bubbling CustomEvents,
        // so they delegate from document.body like every click handler above.
        Object.keys(this.constructor.HOMEPAGE_VIDEO_EVENTS).forEach((name) => {
          this.delegateBody.on(name, this.handleHomepageVideoEvent.bind(this));
        });
    }

    initIntersectionObserver() {
      const settings =  {
        root: null, // Use the viewport as the root
        rootMargin: "0px",
        threshold: 0.1, // Adjust the threshold as per your requirement
      }

      this.intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this), settings);
    }

    sendSortByData(){
        const isDesktop =  new Responsive().matchesBreakpoint('lap-and-up');
        const oldSortByValue = sessionStorage.getItem("SortByValue");
        const sortBy = isDesktop ? 
            document.querySelector('input[name="sort_by"]:checked') : 
            document.querySelector('#sortby.Drawer--wrapper input[name="sort_by"]:checked');    

        if (!sortBy) return;
        const sortByValue = isDesktop ? 
            sortBy.getAttribute('title').replace(' ', '-') : 
            sortBy.value;

        if (!sortByValue || sortByValue === "" || oldSortByValue === sortByValue) return;
        sessionStorage.setItem("SortByValue", sortByValue);

        const payload = {
            event: "sort_applied",
            sort_by: sortByValue,
        };
        dataLayer.push(payload);
    }

    sendFiltersData() {  
        function arraysEqual(arr1, arr2) {
            if (!arr1 || !arr2) return arr1 === arr2;

            return (
                Array.isArray(arr1) &&
                Array.isArray(arr2) &&
                arr1.length === arr2.length &&
                arr1.every((val, index) => val === arr2[index])
            );
        }

        const filtersList = [];
        let oldFiltersList = sessionStorage.getItem("CollectionsFiltersList");
        const filterCheckboxes = document.querySelectorAll('.inputFilterCheckbox');

        if (oldFiltersList) oldFiltersList = JSON.parse(oldFiltersList);

        filterCheckboxes.forEach(checkbox => {
            if (checkbox.checked) {
                filtersList.push(checkbox.dataset.filterName + ":" + checkbox.value);
            }
        });
        
        if (!filtersList.length || arraysEqual(filtersList, oldFiltersList)) return;

        sessionStorage.setItem("CollectionsFiltersList", JSON.stringify(filtersList));

        const payload = {
            event: "filter_applied",
            filter_by: filtersList,
        };
        dataLayer.push(payload);
    }

    clearFiltersData() {
        // Get the previously stored filters before clearing
        let oldFiltersList = sessionStorage.getItem("CollectionsFiltersList");
        if (oldFiltersList) {
            oldFiltersList = JSON.parse(oldFiltersList);
        } else {
            oldFiltersList = [];
        }
        
        // Clear the stored filters
        sessionStorage.setItem("CollectionsFiltersList", JSON.stringify([]));
        
        const payload = {
            event: "clear_filters",
            filter_by: oldFiltersList,
        };
        dataLayer.push(payload);
    }

    interceptCartApi() {
        const originalFetch = window.fetch;
        const _this = this;

        function fetchUrlString(input) {
            if (typeof input === "string") return input;
            if (typeof URL !== "undefined" && input instanceof URL) return input.href;
            if (typeof Request !== "undefined" && input instanceof Request) return input.url;
            return "";
        }

        window.fetch = async function (...args) {
            const [input, init] = args;
            const urlString = fetchUrlString(input);

            // Only wrap Shopify cart AJAX calls — not arbitrary *.js assets (avoids clone/json and errors on non-JSON).
            if (!urlString.includes("/cart")) {
                return originalFetch.apply(this, args);
            }

            if (urlString.includes(`/cart/clear`)) _this.cartClear();
            if (urlString.includes(`/cart/update`)) _this.cartUpdate(init && init.body);

            // Let network failures reject like native fetch (no catch/retry — avoids duplicate requests and misleading logs).
            const response = await originalFetch.apply(this, args);
            if (!response.ok) return response;
            if (urlString === "" || !dataLayer) return response;

            try {
                if (urlString.includes(`/cart/add`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json();
                    _this.cartAdd(json, init);
                } else if (urlString.includes(`/cart/change`)) {
                    const responseClone = response.clone();
                    const json = await responseClone.json();
                    _this.cartChange(json);
                }
            } catch (e) {
                console.error("GTM cart response handling error:", e);
            }

            return response;
        };
    }

    async getCart() {
        const url = (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.rootUrlWithoutSlash)
            ? `${window.themeVariables.routes.rootUrlWithoutSlash}/cart.js`
            : `${window.location.origin}/cart.js`;
        const response = await fetch(url);
        if (!response.ok) return { items: [] };
        return response.json();
    }

    async cartAdd(cart, request) {
        let cartItems = cart.items ? cart.items : [cart];
        cartItems = this.getDataFromRequest(cartItems, request);
        this.productAdded(cartItems);
    }

    async cartUpdate(body) {
      if (!body) return;
      
      let parsedBody;
      try {
        parsedBody = JSON.parse(body);
      } catch (e) {
        console.error('[GTag] Error parsing cart update body:', e);
        return;
      }
      
      const updates = parsedBody?.updates;
      if (!updates || typeof updates !== 'object') {
        console.warn('[GTag] No updates object found in cart update body');
        return;
      }
  
      const oldCart = await this.getCart();
      if (!oldCart || !oldCart.items || !Array.isArray(oldCart.items)) {
        console.warn('[GTag] Invalid cart data received');
        return;
      }
      
      const cartItems = oldCart.items;
      let itemsAdded = [], itemsRemoved = [];

      cartItems.forEach(item => {
          if (!item || !item.key) return;
          
          const newQuantity = updates[item.key];
          if (typeof newQuantity === "number") {
              const change = newQuantity - item.quantity;
              item.quantity = Math.abs(change);
              change < 1 ? itemsRemoved.push(item) : itemsAdded.push(item);
          }
      });

      itemsAdded = this.setLocationShoppingCart(itemsAdded);
      itemsRemoved = this.setLocationShoppingCart(itemsRemoved);

      this.productAdded(itemsAdded);
      this.productsRemoved(itemsRemoved);
    }

    async cartChange(cart) {      
      const itemsAdded = this.setLocationShoppingCart(cart.items_added);
      const itemsRemoved = this.setLocationShoppingCart(cart.items_removed);

      this.productAdded(itemsAdded);
      this.productsRemoved(itemsRemoved);
    }

    productAdded(cartItems) {
        if (!cartItems || !cartItems.length) return;

        const ecommerce = this.buildEcommerce(cartItems);

        const customEvent = {
            event: 'custom_add_to_cart',
            ecommerce
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(customEvent);
        console.log("[GTag] custom_add_to_cart", customEvent);

        // If the StickyAddToCart button initiated this add, also fire a dedicated event
        // with the exact same ecommerce payload (same as custom_add_to_cart).
        const now = Date.now();
        if (this.lastAddToCartButtonId === "StickyAddToCart" && now - this.lastAddToCartAt < 15000) {
          const stickyEvent = { event: "product_sticky_add_to_cart", ecommerce };
          dataLayer.push({ ecommerce: null });
          dataLayer.push(stickyEvent);
          console.log("[GTag] product_sticky_add_to_cart", stickyEvent);
        }

        if (this.lastAddToCartContext === "upsell" && now - this.lastAddToCartAt < 15000) {
          const upsellEvent = { event: "product_recommended_carousel_add_to_cart", ecommerce };
          dataLayer.push({ ecommerce: null });
          dataLayer.push(upsellEvent);
          console.log("[GTag] product_recommended_carousel_add_to_cart", upsellEvent);
        }

        if (this.lastAddToCartContext === "homepage_carousel" && now - this.lastAddToCartAt < 15000 && this.lastHomepageCarouselIndex) {
          const carouselEvent = {
            event: `homepage_carousel${this.lastHomepageCarouselIndex}_add_to_cart`,
            ecommerce
          };
          dataLayer.push({ ecommerce: null });
          dataLayer.push(carouselEvent);
          console.log(`[GTag] ${carouselEvent.event}`, carouselEvent);
        }

        if (this.lastAddToCartContext === "mini_cart_recommendations" && now - this.lastAddToCartAt < 15000) {
          const miniCartEvent = { event: "cart_recommended_carousel_add_to_cart", ecommerce };
          dataLayer.push({ ecommerce: null });
          dataLayer.push(miniCartEvent);
          console.log("[GTag] cart_recommended_carousel_add_to_cart", miniCartEvent);
        }

        // Derive collection context from the current URL (only fire on collection pages)
        const collectionContext = this.getCurrentCollectionContext();

        if (collectionContext) {
          const collectionsEvent = {
            event: 'collections_add_to_cart',
            ecommerce,
            collection_handle: collectionContext.handle,
            collection_url: collectionContext.url
          };

          dataLayer.push({ ecommerce: null });
          dataLayer.push(collectionsEvent);
        }
    }

    getCurrentCollectionContext() {
      try {
        const path = window.location.pathname || "";
        const match = path.match(/\/collections\/([^\/\?]+)/);
        if (!match) return null;

        const handle = match[1];
        const base = window.location.origin || "";

        return {
          handle,
          url: `${base}/collections/${handle}`
        };
      } catch (e) {
        console.error("[GTag] Error detecting collection context:", e);
        return null;
      }
    }

    productsRemoved(cartItems) {
      if (!cartItems || !cartItems.length) return;

      const customEvent = {
            'event': "custom_remove_from_cart",
            'ecommerce': this.buildEcommerce(cartItems)
      };

      dataLayer.push({ ecommerce: null });
      dataLayer.push(customEvent);
    }

    buildEcommerce(cartItems) {
        const items = [];
        let totalPrice = 0;        
        const global_item_id = cartItems[0].list_id;
        const global_item_name = cartItems[0].list_name;
        cartItems.forEach((item) => {            
            const price = typeof item.price === "number" ? item.price / 100 : Number(item.price);
            totalPrice += price * item.quantity;
            
            const product = {
                'item_id': item.sku || "",
                'item_name': item.untranslated_product_title || item.title || "",
                'item_variant': item.untranslated_variant_title || "Default Title",
                'price': price, 
                'quantity': item.quantity,
                'category': item["category"] || "",
                'category2': item["category2"] || "",
                'item_brand': item["item_brand"] || item.vendor || "",
                'item_list_id': item["list_id"] || "",
                'item_list_name': item["list_name"] || ""
            };

            items.push(product);
        });

        return {
            'value': totalPrice,
            'currency': ShopifyAnalytics.meta.currency,
            'item_list_id':global_item_id,
            'item_list_name': global_item_name,
            'items': items
        };
    }

    getDataFromRequest(cartItems, request) {
        const body = request?.body || null;
        if (!body) return cartItems;

        const json = body instanceof FormData
            ? Object.fromEntries(body.entries())
            : JSON.parse(body);

        const quantity = json["quantity"] || 1;
        cartItems[0].quantity = Number(quantity);
        
        cartItems[0]["category"] = json["_gtm-category"] || "";
        cartItems[0]["category2"] = json["_gtm-category2"] || "";
        cartItems[0]["item_brand"] = json["_gtm-item_brand"] || "";
        cartItems[0]["list_id"] = json["_gtm-list_id"] || "";
        cartItems[0]["list_name"] = json["_gtm-list_name"] || "";

        return cartItems;
    }

    setLocationShoppingCart(array) {
      return array.map(item => {            
          item["list_id"] = "shopping_cart"
          item["list_name"] = "Shopping cart"

          // Match DOM: Liquid uses item.key for data-gtm-id; cart API uses key
          const lineItemKey = item.key || item.view_key;
          const lineItem = lineItemKey ? document.querySelector(`[data-gtm-id="${lineItemKey}"]`) : null;
          if (lineItem) {
            const category = lineItem.dataset.gtmCategory;
            const category2 = lineItem.dataset.gtmCategory2;
            const brand = lineItem.dataset.gtmItemBrand;
            if (category) item["category"] = category;
            if (category2) item["category2"] = category2;
            if (brand) item["item_brand"] = brand;
          }
          // Fallback: Shopify cart API includes vendor on each item
          if (!item["item_brand"] && item.vendor) item["item_brand"] = item.vendor;

          return item;
      });
    }

    handleCheckout(event) {
        const button = event.target.closest("button[name='checkout']");
        if (!button) return;

        const script = button.parentElement.querySelector('[type="application/ld+json"]');
        const settings = JSON.parse(script.innerText);

        if (!dataLayer || !settings) return;

        dataLayer.push({ ecommerce: null });
        dataLayer.push(settings);

    }

    handleSelectItem(event) {
      let target = event.target.closest('[data-action="ga-send-select"]') || event.target.closest('[data-ga-action="ga-send-select"]');
      if (!target || !dataLayer) return;

      const settings = JSON.parse(target.dataset.selectSettings);
      if (!settings) return;

      const customEvent = { 
          event: "custom_select_item",
          ecommerce: {
              item_list_id: settings.list_id,
              item_list_name: settings.list_name,
              currency: ShopifyAnalytics.meta.currency,
              items: [settings]
          }
      }
  
      dataLayer.push({ ecommerce: null });
      dataLayer.push(customEvent);
  }

    handleCartContinueShopping(event) {
      if (!dataLayer) return;

      const payload = {
        event: "cart_continue_shopping",
        link_url: event.currentTarget?.href || window.location.href
      };

      dataLayer.push(payload);
    }

    handleCollectionPageClick(event) {
      if (!dataLayer) return;

      const button = event.target.closest(".metaButtons--wrapper .metaButton");
      if (!button) return;

      // Purple button contains the module title as the span text (see collection section markup).
      const moduleTitle =
        button.querySelector("span")?.textContent?.trim() ||
        button.textContent?.trim() ||
        "";

      if (!moduleTitle) return;

      // Only fire on real collection pages.
      const collectionContext = this.getCurrentCollectionContext();
      if (!collectionContext) return;

      const payload = {
        event: "collection_page_click",
        module_title: moduleTitle,
        // GA4 dimension mapping expects `module_distributor` as well.
        module_distributor: moduleTitle,
      };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] collection_page_click", payload);
      dataLayer.push(payload);
    }

    handleProductPageClick(event) {
      if (!dataLayer) return;

      const slide = event.target.closest(".product__info-headings .swiper-slide");
      if (!slide) return;

      const moduleTitle =
        slide.querySelector("span")?.textContent?.trim() ||
        slide.textContent?.trim() ||
        "";

      if (!moduleTitle) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = {
        event: "product_page_click",
        tab_title: moduleTitle,
        product_handle: match[1],
      };

      console.log("[GTag] product_page_click", payload);
      dataLayer.push(payload);
    }

    handleQuizStartClick(event) {
      if (!dataLayer) return;

      const payload = {
        event: "quiz_start",
      };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] quiz_start", payload);
      dataLayer.push(payload);
    }

    handleQuizEmailStepClick(event) {
      if (!dataLayer) return;

      const input = event.target;
      if (!input || input.tagName !== "INPUT") return;

      // Fire only once per input element, and only when the user has started typing.
      if (input.dataset.gtmQuizEmailStepFired === "true") return;
      if (!input.value || input.value.trim() === "") return;

      input.dataset.gtmQuizEmailStepFired = "true";

      const payload = { event: "quiz_email_step" };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] quiz_email_step", payload);
      dataLayer.push(payload);
    }

    handleQuizCompletionClick(event) {
      if (!dataLayer) return;

      const quizRoot = event.target?.closest?.("div[oke-quiz]");
      if (quizRoot) quizRoot.dataset.gtmQuizCompletionFired = "true";

      const payload = { event: "quiz_completion" };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] quiz_completion", payload);
      dataLayer.push(payload);
    }

    handleQuizCompletionSubmit(event) {
      if (!dataLayer) return;

      const form = event.target;
      if (!form || !(form instanceof HTMLFormElement)) return;

      const quizRoot = form.closest("div[oke-quiz]");
      if (!quizRoot) return;

      // Dedupe: avoid firing twice when click triggers submit.
      if (quizRoot.dataset.gtmQuizCompletionFired === "true") return;

      const nextButton = quizRoot.querySelector("button.c-questionsForm-next--email");
      if (!nextButton) return;

      const emailInput = quizRoot.querySelector('c-emailInput input[type="email"]');
      if (!emailInput) return;

      if (!emailInput.value || emailInput.value.trim() === "") return;

      quizRoot.dataset.gtmQuizCompletionFired = "true";

      const payload = { event: "quiz_completion" };

      // Debug: verify payload sent to dataLayer.
      console.log("[GTag] quiz_completion", payload);
      dataLayer.push(payload);
    }

    handleProductImageGalleryClick(event) {
      if (!dataLayer) return;

      const isDesktop = new Responsive().matchesBreakpoint("lap-and-up");
      if (!isDesktop) return;

      const media = event.target.closest(".product__media");
      if (!media) return;

      const payload = { event: "product_image_gallery" };
      console.log("[GTag] product_image_gallery", payload);
      dataLayer.push(payload);
    }

    handleProductImageSwipeTouchStart(event) {
      if (!dataLayer) return;

      const isDesktop = new Responsive().matchesBreakpoint("lap-and-up");
      if (isDesktop) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const slider = event.target?.closest?.(".flickity-slider") || event.target?.closest?.("flickity-carousel.product__media-list")?.querySelector?.(".flickity-slider");
      if (!slider) return;

      // Only for the main product media carousel
      if (!slider.closest("flickity-carousel.product__media-list")) return;

      // Dedupe quick repeated touchstarts (some browsers can emit multiple).
      const now = Date.now();
      const last = Number(slider.dataset.gtmProductImageSwipeLastTouchStart || 0);
      if (now - last < 600) return;
      slider.dataset.gtmProductImageSwipeLastTouchStart = String(now);

      const payload = { event: "product_image_swipe" };
      console.log("[GTag] product_image_swipe", payload);
      dataLayer.push(payload);
    }

    handleProductRatingsInteraction(event) {
      if (!dataLayer) return;

      const rating = event.target?.closest?.("div[oke-star-rating]");
      if (!rating) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_ratings" };
      console.log("[GTag] product_ratings", payload);
      dataLayer.push(payload);
    }

    handleProductSizeClick(event) {
      if (!dataLayer) return;

      const link = event.target?.closest?.('.connected-items__wrapper a.SelectboxLabelNormal[data-title]');
      if (!link) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const title = link.dataset.title?.trim() || "";
      if (!title) return;

      const payload = {
        event: "product_size",
        size_title: title,
      };

      console.log("[GTag] product_size", payload);
      dataLayer.push(payload);
    }

    handleProductColorClick(event) {
      if (!dataLayer) return;

      const swatch = event.target?.closest?.(
        ".SelectBox--WrapperColor a.SelectboxCircle[data-title], .SelectBox--InnerWrapperColor a.SelectboxCircle[data-title]"
      );
      if (!swatch) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const title = swatch.dataset.title?.trim() || "";
      if (!title) return;

      const payload = {
        event: "product_color",
        data_title: title,
      };

      console.log("[GTag] product_color", payload);
      dataLayer.push(payload);
    }

    handleProductQuizCTAClick(event) {
      if (!dataLayer) return;

      const button = event.target?.closest?.(".productInfo--wraper .quiz--button");
      if (!button) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_quiz_CTA" };
      console.log("[GTag] product_quiz_CTA", payload);
      dataLayer.push(payload);
    }

    handleProductTabsClick(event) {
      if (!dataLayer) return;

      // Don't treat ingredient "view all" interactions as a tab click.
      if (event.target?.closest?.(".Product--ExpandButton")) return;

      const tab = event.target?.closest?.(".product__info-wrapper .swiper-slide");
      if (!tab) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const tabTitle =
        tab.querySelector("span")?.textContent?.trim() ||
        tab.textContent?.trim() ||
        "";

      if (!tabTitle) return;

      const payload = {
        event: "product_tabs",
        tabs_title: tabTitle,
      };

      console.log("[GTag] product_tabs", payload);
      dataLayer.push(payload);
    }

    handleProductViewAllIngredientsClick(event) {
      if (!dataLayer) return;

      const button = event.target?.closest?.(".Product--ExpandButton");
      if (!button) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_tabs_view_all_ingredients" };
      console.log("[GTag] product_tabs_view_all_ingredients", payload);
      dataLayer.push(payload);
    }

    handleProductVideoClick(event) {
      if (!dataLayer) return;

      const videoSection = event.target?.closest?.("#section-firework-embed, .section-firework-embed, section-firework-embed");
      if (!videoSection) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_video_click" };
      console.log("[GTag] product_video_click", payload);
      dataLayer.push(payload);
    }

    handleProductReviewsTabClick(event) {
      if (!dataLayer) return;

      const tab = event.target?.closest?.(".oke-w-navBar-item");
      if (!tab) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_reviews_tab" };
      console.log("[GTag] product_reviews_tab", payload);
      dataLayer.push(payload);
    }

    handleProductRecommendedCarouselTabClick(event) {
      if (!dataLayer) return;

      const tab = event.target?.closest?.(".upsell__block .upsell__tab");
      if (!tab) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const tabTitle = tab.textContent?.trim() || "";
      if (!tabTitle) return;

      const payload = {
        event: "product_recommended_carousel_tabs",
        tabs_title: tabTitle,
      };

      console.log("[GTag] product_recommended_carousel_tabs", payload);
      dataLayer.push(payload);
    }

    handleProductRecommendedCarouselViewMoreClick(event) {
      if (!dataLayer) return;

      const button = event.target?.closest?.('.upsell__block [data-action="show-more"]');
      if (!button) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = {
        event: "product_recommended_carousel_view_more",
        tab_index: button.getAttribute("data-index") || "",
      };

      console.log("[GTag] product_recommended_carousel_view_more", payload);
      dataLayer.push(payload);
    }

    handleCartRecommendedCarouselArrowsClick(event) {
      if (!dataLayer) return;

      const button = event.target?.closest?.(".mini-cart__recommendations .swiperButton");
      if (!button) return;

      const payload = { event: "cart_recommended_carousel_arrows" };
      console.log("[GTag] cart_recommended_carousel_arrows", payload);
      dataLayer.push(payload);
    }

    handleProductBlogSeeAllArticlesClick(event) {
      if (!dataLayer) return;

      const button = event.target?.closest?.(".blogPosts--container .seeAll--Button");
      if (!button) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_blog_see_all_articles" };
      console.log("[GTag] product_blog_see_all_articles", payload);
      dataLayer.push(payload);
    }

    handleProductBlogReadClick(event) {
      if (!dataLayer) return;

      const button = event.target?.closest?.(".blogPosts--container .article--Button");
      if (!button) return;

      // Only fire on product pages.
      const path = window.location.pathname || "";
      const match = path.match(/\/products\/([^\/\?\#]+)/);
      if (!match) return;

      const payload = { event: "product_blog_read" };
      console.log("[GTag] product_blog_read", payload);
      dataLayer.push(payload);
    }

    handleCartRecommendedCarouselClick(event) {
      if (!dataLayer) return;

      const cardLink = event.target?.closest?.(".productRecommendations .cardProduct");
      if (!cardLink) return;

      const productCard = cardLink.closest("product-card") || cardLink.closest(".product-item") || cardLink.parentElement;

      const productTitle =
        productCard?.querySelector?.(".cardInformation--Meta span.u-h7")?.textContent?.trim() ||
        productCard?.querySelector?.(".cardInformation--Meta")?.textContent?.trim() ||
        cardLink.getAttribute("aria-label")?.trim() ||
        "";

      const payload = {
        event: "cart_recommended_carousel_click",
        product_title: productTitle,
      };

      console.log("[GTag] cart_recommended_carousel_click", payload);
      dataLayer.push(payload);
    }

    handleNavigationHairQuizClick(event) {
      if (!dataLayer) return;

      // The whole card is one link and the CTA sits inside it, so this covers both.
      const card = event.target?.closest?.("a.mobile-menu-quiz");
      if (!card) return;

      const payload = { event: "navigation_hair_quiz" };
      console.log("[GTag] navigation_hair_quiz", payload);
      dataLayer.push(payload);
    }

    /**
     * Market / country selector in the header.
     *
     * Three markups reach the same GA4 event: the market list and the domain links
     * in location-selector.liquid, and the desktop cross-border links in
     * country-selector.liquid. Domain links carry no ISO code, so market_name is
     * always sent and the event is never left without a usable value.
     *
     * @param {MouseEvent} event - Delegated click on a selector option.
     * @returns {void}
     */
    handleHomepageMarketSelectorClick(event) {
      if (!dataLayer || !this.isHomepage()) return;

      const option = event.target?.closest?.(
        '[data-action="selectbox-change"][data-selectboxid="country_code_selector"], [data-action="selectbox-link"], [data-gtm-market]'
      );
      if (!option) return;

      const countryCode = (option.dataset.value || option.dataset.gtmMarket || "").toUpperCase();
      const marketName = option.dataset.name || option.dataset.gtmMarketName || option.textContent?.trim().replace(/\s+/g, " ") || "";
      if (!countryCode && !marketName) return;

      if (this.homepageFiredRecently(`homepage_market_selector:${countryCode}:${marketName}`)) return;

      this.homepagePush("homepage_market_selector", {
        selected_country: countryCode,
        market_name: marketName,
      });
    }

    /**
     * Firework SDK CustomEvents we map straight to a GA4 event name.
     *
     * Only events whose meaning is unambiguous are listed. `fw:player:transition`
     * is deliberately absent: it is a fullscreen *toggle*, so it fires on minimize
     * too and would over-report homepage_ugc_video_maximize. Maximize and the feed
     * arrows are handled by the click fallback instead.
     *
     * Load the homepage with ?gtmDebug=firework to log every Firework event and
     * click path to the console when re-verifying these against the live widget.
     */
    static FIREWORK_SDK_EVENTS = {
      "fw:video:user-played": "homepage_ugc_video_start",
      "fw:video:click-cta": "homepage_ugc_video_add_CTA",
      "fw:player:navigate": "homepage_ugc_video_product_click",
    };

    /* ==================================================================
     * Homepage tracking
     * ------------------------------------------------------------------
     * Elements are tagged in Liquid with data-gtm-* attributes so these
     * handlers survive class/markup changes in the sections:
     *   [data-gtm-banner]            slideshow + image/video/text banners
     *   [data-gtm-quick-collections] quick collections bar under the hero
     *   [data-gtm-carousel]          featured-collections product carousel
     *   [data-gtm-collectionbox]     "shop by concern" showcase
     *   [data-gtm-app]               app sections (Instafeed, Firework)
     *
     * Banner and carousel positions are derived from DOM order, so the
     * hero banner is always banner 1 and merchandisers can reorder the
     * homepage in the theme editor without the events going stale.
     * ================================================================== */

    /**
     * @returns {boolean} True when the current page is the homepage (template index).
     */
    isHomepage() {
      const pageType = window.themeVariables?.settings?.pageType;
      if (pageType) return pageType === "index";

      // Fallback for pages rendered without the theme variables snippet.
      const path = (window.location.pathname || "").replace(/\/+$/, "");
      return path === "" || /^\/[a-z]{2}(-[a-z]{2})?$/i.test(path);
    }

    /**
     * Position of an element among all elements matching a selector, in DOM order.
     * This is what makes the hero always banner 1 and lets merchandisers reorder
     * the homepage without the event names going stale.
     *
     * @param {Element} element - The element to locate.
     * @param {string} selector - Selector describing the set to rank within.
     * @returns {number} 1-based position, or 0 when the element is not in the set.
     */
    getHomepageElementIndex(element, selector) {
      if (!element) return 0;
      const all = Array.from(document.querySelectorAll(selector));
      const index = all.indexOf(element);
      return index === -1 ? 0 : index + 1;
    }

    /**
     * Guards against one interaction being reported twice, e.g. when a Firework SDK
     * event and the click fallback both describe the same tap.
     *
     * @param {string} key - Identifies the interaction, not just the event name.
     * @param {number} [windowMs=800] - How long a repeat counts as the same interaction.
     * @returns {boolean} True when this key already fired inside the window.
     */
    homepageFiredRecently(key, windowMs = 800) {
      const now = Date.now();
      const last = this.recentHomepageEvents[key] || 0;
      if (now - last < windowMs) return true;
      this.recentHomepageEvents[key] = now;
      return false;
    }

    /**
     * Pushes one homepage event to the dataLayer, logging it like the rest of this layer.
     *
     * @param {string} eventName - GA4 event name from the agreed spec.
     * @param {Object} [params] - Event parameters merged into the payload.
     * @returns {void}
     */
    homepagePush(eventName, params) {
      if (!dataLayer || !eventName) return;

      const payload = Object.assign({ event: eventName }, params || {});
      console.log(`[GTag] ${eventName}`, payload);
      dataLayer.push(payload);
    }

    /**
     * Hero / banner CTA and hero slider clicks.
     * Banner 1 is the hero, so it reports homepage_herobanner_CTA; banners 2 and 3
     * report homepage_banner{n}_CTA. Picture and pagination clicks are only
     * reported for the hero.
     *
     * @param {MouseEvent} event - Delegated click on the section wrapper.
     * @returns {void}
     */
    handleHomepageBannerClick(event) {
      if (!dataLayer || !this.isHomepage()) return;

      const banner = event.target?.closest?.("[data-gtm-banner]");
      if (!banner) return;

      const bannerIndex = this.getHomepageElementIndex(banner, "[data-gtm-banner]");
      if (!bannerIndex) return;

      const slide = event.target?.closest?.("[data-gtm-slide-index]");
      const slideIndex = Number(slide?.dataset?.gtmSlideIndex || 1);
      const slideTitle = slide?.dataset?.gtmSlideTitle || "";

      // `.Button` covers every CTA rendered through the buttons snippet;
      // `.slideshow__slide-inner` is the whole-slide link used when a slide
      // has a link but no button text.
      const cta = event.target?.closest?.(".Button, a.slideshow__slide-inner");

      if (cta) {
        const eventName = bannerIndex === 1 ? "homepage_herobanner_CTA" : `homepage_banner${bannerIndex}_CTA`;
        if (this.homepageFiredRecently(`${eventName}:${slideIndex}`)) return;

        this.homepagePush(eventName, {
          banner_position: bannerIndex,
          banner_title: slideTitle,
          slide_index: slideIndex,
          cta_text: cta.textContent?.trim().replace(/\s+/g, " ") || "",
          link_url: cta.getAttribute("href") || "",
        });
        return;
      }

      // Picture / pagination interactions are only reported for the hero banner.
      if (bannerIndex !== 1) return;

      const dot = event.target?.closest?.(".slideshow__progress-bar");
      const picture = event.target?.closest?.(".slideshow__slide");
      if (!dot && !picture) return;

      const interaction = dot ? "pagination" : "image";
      const targetSlide = dot ? Number(dot.dataset.gtmSlideIndex || 0) : slideIndex;
      if (this.homepageFiredRecently(`homepage_herobanner_slider_click:${interaction}:${targetSlide}`)) return;

      this.homepagePush("homepage_herobanner_slider_click", {
        interaction,
        slide_index: targetSlide,
        banner_title: slideTitle,
      });
    }

    /**
     * Quick collections bar under the hero. Sends the collection name as the
     * custom dimension the spec asks for.
     *
     * @param {MouseEvent} event - Delegated click on the section wrapper.
     * @returns {void}
     */
    handleHomepageQuickCollectionsClick(event) {
      if (!dataLayer || !this.isHomepage()) return;

      const link = event.target?.closest?.("[data-gtm-quick-collections] [data-gtm-collection-title]");
      if (!link) return;

      const collectionName = link.dataset.gtmCollectionTitle?.trim() || link.textContent?.trim() || "";
      if (!collectionName) return;

      if (this.homepageFiredRecently(`homepage_quickcollections:${collectionName}`)) return;

      this.homepagePush("homepage_quickcollections", {
        collection_name: collectionName,
        collection_url: link.getAttribute("href") || "",
      });
    }

    /**
     * Product clicks inside a homepage product carousel. Add to cart is skipped
     * here and reported from the cart API response instead, so it can carry ecommerce.
     *
     * @param {MouseEvent} event - Delegated click on the section wrapper.
     * @returns {void}
     */
    handleHomepageCarouselClick(event) {
      if (!dataLayer || !this.isHomepage()) return;

      const carousel = event.target?.closest?.("[data-gtm-carousel]");
      if (!carousel) return;

      // Add to cart is reported from the cart API response instead (see productAdded),
      // so the event can carry the same ecommerce payload as custom_add_to_cart.
      if (event.target?.closest?.('[data-action="add-to-cart"], button[type="submit"]')) return;

      const link = event.target?.closest?.("a.cardProduct, a.cardInformation--Meta");
      if (!link) return;

      const carouselIndex = this.getHomepageElementIndex(carousel, "[data-gtm-carousel]");
      if (!carouselIndex) return;

      const card = link.closest("product-card") || link.closest(".product-item");
      const productTitle =
        card?.querySelector?.(".cardInformation--Meta span.u-h7")?.textContent?.trim() ||
        card?.querySelector?.(".card__content h3")?.textContent?.trim() ||
        "";

      const href = link.getAttribute("href") || "";
      const handleMatch = href.match(/\/products\/([^\/\?\#]+)/);
      const slide = link.closest(".swiper-slide");

      const eventName = `homepage_carousel${carouselIndex}_product_click`;
      if (this.homepageFiredRecently(`${eventName}:${href}`)) return;

      this.homepagePush(eventName, {
        carousel_position: carouselIndex,
        carousel_title: carousel.dataset.gtmCarouselTitle || "",
        product_title: productTitle,
        product_handle: handleMatch ? handleMatch[1] : "",
        item_position: Number(slide?.dataset?.mediaId || 0),
        link_url: href,
      });
    }

    /**
     * "Shop by concern" boxes. The custom dimension is the *block* heading
     * ("SCALP CARE"), not the section heading, so the events can be told apart.
     *
     * @param {MouseEvent} event - Delegated click on the section wrapper.
     * @returns {void}
     */
    handleHomepageCollectionBoxClick(event) {
      if (!dataLayer || !this.isHomepage()) return;

      const box = event.target?.closest?.("[data-gtm-collectionbox]");
      const button = event.target?.closest?.("[data-gtm-collectionbox] [data-gtm-collection-title]");
      if (!box || !button) return;

      const collectionName = button.dataset.gtmCollectionTitle?.trim() || "";
      if (!collectionName) return;

      if (this.homepageFiredRecently(`homepage_collectionbox_click:${collectionName}`)) return;

      this.homepagePush("homepage_collectionbox_click", {
        section_title: box.dataset.gtmCollectionboxTitle || "",
        collection_name: collectionName,
        collection_url: button.getAttribute("href") || "",
      });
    }

    /**
     * Instagram UGC tiles in the "join the community" section (Instafeed app).
     *
     * @param {MouseEvent} event - Delegated click on the section wrapper.
     * @returns {void}
     */
    handleHomepageAppSectionClick(event) {
      if (!dataLayer || !this.isHomepage()) return;

      const container = event.target?.closest?.("[data-gtm-app]");
      if (!container) return;

      // Instafeed renders the "join the community" grid. Firework clicks are
      // handled separately because they happen inside a shadow root.
      if (!(container.dataset.gtmApp || "").toLowerCase().includes("instafeed")) return;

      const item = event.target?.closest?.('a, img, [class*="instafeed"]');
      if (!item) return;

      const link = event.target?.closest?.("a");
      const links = Array.from(container.querySelectorAll("a"));
      const linkUrl = link?.getAttribute("href") || "";

      if (this.homepageFiredRecently(`homepage_rrss_ugc_click:${linkUrl}`)) return;

      this.homepagePush("homepage_rrss_ugc_click", {
        link_url: linkUrl,
        item_position: link ? links.indexOf(link) + 1 : 0,
      });
    }

    /* ==================================================================
     * Firework UGC video widget
     * ------------------------------------------------------------------
     * Firework renders its player as web components with a closed-ish
     * shadow DOM, so ordinary event delegation never sees the clicks.
     * Two complementary strategies are used:
     *   1. the SDK's own `fw:*` CustomEvents, when the app emits them
     *   2. a capture-phase click listener that reads composedPath(), which
     *      does cross shadow boundaries
     * Both feed the same mapper and share a dedupe window, so whichever
     * fires first wins and the other is dropped.
     *
     * The exact SDK event names and internal class names are owned by
     * Firework and can change. Append ?gtmDebug=firework to the homepage
     * URL to log every Firework event and click path to the console, which
     * is how the mapping below should be re-verified against the live app.
     * ================================================================== */

    /**
     * Subscribes to the Firework widget's video interactions.
     *
     * Firework dispatches its CustomEvents on `document`, which sits above the
     * theme's Delegate root (document.body), so these cannot go through Delegate.
     * The click fallback needs composedPath() for the same reason Delegate cannot
     * be used there: it has to cross the widget's shadow boundary.
     *
     * @returns {void}
     */
    initFireworkTracking() {
      this.fireworkDebug = /[?&]gtmDebug=firework/i.test(window.location.search || "");

      const sdkHandler = this.handleFireworkSdkEvent.bind(this);
      Object.keys(this.constructor.FIREWORK_SDK_EVENTS).forEach((name) => {
        window.addEventListener(name, sdkHandler, true);
        document.addEventListener(name, sdkHandler, true);
      });

      document.addEventListener("click", this.handleFireworkClick.bind(this), true);
    }

    /**
     * Turns a DOM descriptor ("aria-label class id …") from inside the Firework
     * player into one of the agreed homepage_ugc_video_* event names. Used only by
     * the click fallback; SDK events are mapped by name via FIREWORK_SDK_EVENTS.
     *
     * @param {string} value - Concatenated attributes of a single element.
     * @returns {string|null} The GA4 event name, or null when nothing matched.
     */
    mapFireworkInteraction(value) {
      const text = ` ${String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()} `;
      if (text.trim() === "") return null;

      const has = (...words) => words.some((word) => text.includes(` ${word} `));

      if (has("fullscreen", "maximize", "maximise", "maximized", "expand", "expanded")) {
        return "homepage_ugc_video_maximize";
      }
      if (text.includes(" add to cart ") || text.includes(" shop now ") || has("cta", "addtocart", "plus")) {
        return "homepage_ugc_video_add_CTA";
      }
      if (has("product", "products")) {
        return "homepage_ugc_video_product_click";
      }
      if (has("next", "prev", "previous", "arrow", "chevron", "navigate", "navigation")) {
        return "homepage_ugc_video_arrow_slider";
      }
      if (has("play", "start", "started")) {
        return "homepage_ugc_video_start";
      }

      return null;
    }

    /**
     * Relays one Firework SDK CustomEvent to the dataLayer.
     *
     * @param {CustomEvent} event - The `fw:*` event Firework dispatched on document.
     * @returns {void}
     */
    handleFireworkSdkEvent(event) {
      if (!dataLayer || !this.isHomepage()) return;

      const eventName = this.constructor.FIREWORK_SDK_EVENTS[event?.type] || null;

      if (this.fireworkDebug) {
        console.log("[GTag][Firework] sdk event", event?.type, event?.detail, "->", eventName);
      }

      if (!eventName) return;
      if (this.homepageFiredRecently(`firework:${eventName}`, 1000)) return;

      const detail = event?.detail || {};
      this.homepagePush(eventName, {
        source: "firework_sdk",
        firework_event: event?.type || "",
        video_id: detail.videoId || detail.video_id || "",
        product_id: detail.productId || detail.product_id || "",
      });
    }

    /**
     * Fallback for Firework interactions that have no unambiguous SDK event —
     * currently maximize and the feed arrows. Reads composedPath() so it can see
     * through the widget's shadow root, then matches the innermost element that
     * describes itself.
     *
     * @param {MouseEvent} event - A capture-phase click anywhere on the document.
     * @returns {void}
     */
    handleFireworkClick(event) {
      if (!dataLayer || !this.isHomepage()) return;

      const path = typeof event.composedPath === "function" ? event.composedPath() : [];
      if (!path.length) return;

      const insideFirework = path.some((node) => {
        const tagName = node?.tagName?.toLowerCase?.() || "";
        if (tagName.startsWith("fw-")) return true;
        return String(node?.dataset?.gtmApp || "").toLowerCase().includes("firework");
      });
      if (!insideFirework) return;

      // Walk outwards from the clicked node: the innermost element that
      // describes itself (aria-label, test id, class) wins.
      let eventName = null;
      let matchedOn = "";

      for (const node of path) {
        if (!node || node.nodeType !== 1) continue;

        const descriptor = [
          node.getAttribute?.("aria-label"),
          node.getAttribute?.("data-testid"),
          node.getAttribute?.("title"),
          node.getAttribute?.("part"),
          node.id,
          typeof node.className === "string" ? node.className : "",
        ]
          .filter(Boolean)
          .join(" ");

        const mapped = this.mapFireworkInteraction(descriptor);
        if (mapped) {
          eventName = mapped;
          matchedOn = descriptor;
          break;
        }
      }

      if (this.fireworkDebug) {
        console.log(
          "[GTag][Firework] click path",
          path.map((node) => node?.tagName?.toLowerCase?.() || String(node)),
          "->",
          eventName,
          matchedOn
        );
      }

      if (!eventName) return;
      if (this.homepageFiredRecently(`firework:${eventName}`, 1000)) return;

      this.homepagePush(eventName, {
        source: "firework_dom",
        matched_on: matchedOn.replace(/\s+/g, " ").trim().slice(0, 120),
      });
    }

    /* ==================================================================
     * Homepage hero / banner videos
     * ------------------------------------------------------------------
     * VideoComponent (theme.js) re-dispatches its Video.js player events as
     * bubbling `videojs:*` CustomEvents. Video.js itself triggers on an internal
     * event bus element that never reaches the DOM, so there is nothing to
     * delegate to without that bridge.
     *
     * Autoplay and loop filtering live in VideoComponent next to the player
     * state they depend on, so anything arriving here is already reportable.
     * ================================================================== */

    /** `videojs:*` CustomEvent -> homepage event suffix. */
    static HOMEPAGE_VIDEO_EVENTS = {
      "videojs:reached": "impression",
      "videojs:started": "start",
      "videojs:progress": "progress",
      "videojs:completed": "complete",
      "videojs:unmuted": "unmute",
    };

    /**
     * Describes a homepage video, or rejects it when it is not one we track.
     * Banner 1 is the hero, matching the naming the banner CTA events use.
     *
     * @param {Element} component - The <video-component> the event came from.
     * @returns {Object|null} Event name prefix and shared parameters, or null.
     */
    resolveHomepageVideo(component) {
      if (!dataLayer || !this.isHomepage() || !component) return null;

      const banner = component.closest("[data-gtm-banner]");
      if (!banner) return null;

      const bannerIndex = this.getHomepageElementIndex(banner, "[data-gtm-banner]");
      if (!bannerIndex) return null;

      const slide = component.closest("[data-gtm-slide-index]");

      return { 
        prefix: bannerIndex === 1 ? "homepage_herobanner_video" : `homepage_banner${bannerIndex}_video`,
        params: {
          banner_position: bannerIndex,
          banner_title: slide?.dataset?.gtmSlideTitle || "",
          slide_index: Number(slide?.dataset?.gtmSlideIndex || 0),
          video_id: component.querySelector("video")?.id || "",
        },
      };
    }

    /**
     * Reports one video interaction relayed by VideoComponent.
     *
     * @param {CustomEvent} event - A `videojs:*` event.
     * @returns {void}
     */
    handleHomepageVideoEvent(event) {
      const suffix = this.constructor.HOMEPAGE_VIDEO_EVENTS[event.type];
      if (!suffix) return;

      const context = this.resolveHomepageVideo(event.target?.closest?.("video-component"));
      if (!context) return;

      const params = Object.assign({}, context.params);
      if (event.detail?.id) params.video_id = event.detail.id;
      if (event.detail?.percent) params.percent_watched = event.detail.percent;

      this.homepagePush(`${context.prefix}_${suffix}`, params);
    }

  domChanged() {
      const allIntersections = document.body.querySelectorAll('[data-gtag-intersection][data-observed="false"]');

      allIntersections.forEach((item) => {
        // Always use intersection observer so events re-fire when element scrolls back into view
        this.intersectionObserver.observe(item);
        item.dataset.observed = "true";
      });
    }
    
    processIntersectionElement(element) {
      const scriptElement = element.querySelector('script[type="application/ld+json"]');
      if (!scriptElement || !dataLayer) return;
      
      try {
        const data = JSON.parse(scriptElement.textContent);
        if (!data) return;

        dataLayer.push({ ecommerce: null });  
        dataLayer.push(data);
      } catch (e) {
        console.error("[GTag] Error parsing intersection element data:", e);
      }
    }
  
    observeDOMChanges() {
      const domMutationObserver = new MutationObserver((mutationsList, observer) => {
        // For each mutation record, we look for added nodes
        for (const mutation of mutationsList) {
          if (mutation.addedNodes.length) {
            // If there are added nodes, we call domChanged to check for new items
            this.domChanged();
          }
        }
      });
  
      // Start observing the document body for added nodes and subtree modifications
      domMutationObserver.observe(document.body, {
        childList: true, // Observe direct children
        subtree: true, // and all descendants
        attributes: false // Set to true if you also want to observe attribute changes
      });
    }
    
    handleIntersection(entries) {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
              this.processIntersectionElement(entry.target);
              // Do not unobserve – keep observing so event re-fires when user scrolls back into view
          }
        });
    }
}

new gTagEvents();
